import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AlwaysTrue, And, Or, Rule, RuleInterface, StringEquals, StringListContains } from '@vendasta/automata';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { CommonTriggerPanelComponent } from '../../../../common/common-trigger-panel.component';
import {
  changeTypeVariablePath,
  CREATED,
  CrmCreatedModifiedSelectorService,
  fieldsChangedVariablePath,
  UPDATED,
} from '../../../crm-common/crm-created-modified-selector/crm-created-modified-selector.service';
import {
  ActionComparison,
  CrmCreatedModifiedSelectorComponent,
} from '../../../crm-common/crm-created-modified-selector/crm-created-modified-selector.component';
import { WORKFLOW_TRIGGERS } from '../../../../../component-loader/trigger-definitions';
import {
  CRMCompanyCreatedOrModified,
  CRMContactCreatedOrModified,
  CRMOpportunityCreatedOrModified,
} from '../../../../../component-loader/constants';
import { AutomationReplacementChipsComponent } from '../../../../../../common/components';
import { CrmFieldsSelectorService } from '../../../../../../data-services/crm-fields-selector.service';
import { supportedSelectionFieldTypes } from '../../../../filters';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

// Map of triggers that are a CRM record
// This means it fires on the RecordChange activity and they have custom fields on their CRM object type
const RECORD_CHANGE_TRIGGERS: Map<string, string> = new Map([
  [CRMContactCreatedOrModified, 'Contact'],
  [CRMCompanyCreatedOrModified, 'Company'],
  [CRMOpportunityCreatedOrModified, 'Opportunity'],
]);

@Component({
  selector: 'automata-crm-activity-created-modified-panel',
  imports: [
    CommonModule,
    TranslateModule,
    CrmCreatedModifiedSelectorComponent,
    AutomationReplacementChipsComponent,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
  ],
  templateUrl: './crm-activity-created-modified-panel.component.html',
  styleUrls: ['./crm-activity-created-modified-panel.component.scss'],
})
export class CrmActivityCreatedModifiedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  action = ActionComparison.Any;
  comparison = ActionComparison;
  nameKey = '';
  labelDisplay?: string;
  crmSubType?: string;
  isCRMRecordChangeTrigger = false;
  onlyStandardFields = false;

  supportedFieldTypes = supportedSelectionFieldTypes;
  crmFieldsSelectorService = inject(CrmFieldsSelectorService);

  formControl = new FormControl<string[] | undefined>([]);

  constructor(private crmCreatedModifiedSelectorService: CrmCreatedModifiedSelectorService) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.automation.isTemplate) {
      this.onlyStandardFields = true;
    }
    const triggerDefinitions: { [taskKey: string]: any } = WORKFLOW_TRIGGERS;
    this.nameKey = triggerDefinitions?.[this.taskDefinitionId]?.name;
    this.labelDisplay = triggerDefinitions?.[this.taskDefinitionId]?.labelDisplay;
    this.isCRMRecordChangeTrigger = !!RECORD_CHANGE_TRIGGERS.get(this.taskDefinitionId);
    this.crmSubType = RECORD_CHANGE_TRIGGERS.get(this.taskDefinitionId);

    this.savingEnabled$.next(true);

    if (!this.triggerChoices?.rules || (this.triggerChoices.rules || []).length === 0) {
      return;
    }
    this.action = this.crmCreatedModifiedSelectorService.findActionComparison(this.triggerChoices.rules[0]);
    if (this.isCRMRecordChangeTrigger) {
      this.formControl.setValue(
        this.crmCreatedModifiedSelectorService.findFieldChangedValue(this.triggerChoices.rules[0]) || [],
      );
    }
  }

  fillRuleValues(_: RuleInterface[]): RuleInterface[] {
    return [this.changeTypeRule().toRuleInterface()];
  }

  getBaseRules(): RuleInterface[] {
    return [this.changeTypeRule().toRuleInterface()];
  }

  private changeTypeRule(): Rule {
    switch (this.action) {
      case ActionComparison.Created:
        return new And([new StringEquals(changeTypeVariablePath, CREATED)]);
      case ActionComparison.Modified:
        // Modified rule requires field selection(s) for contact/company record change triggers
        if (this.isCRMRecordChangeTrigger) {
          return new And([
            new StringEquals(changeTypeVariablePath, UPDATED),
            new StringListContains(fieldsChangedVariablePath, this.formControl.value),
          ]);
        }
        return new And([new StringEquals(changeTypeVariablePath, UPDATED)]);
      default:
        // Created or modified rule is different for contact/company record change triggers
        if (this.isCRMRecordChangeTrigger) {
          return new Or([
            new StringEquals(changeTypeVariablePath, CREATED),
            new StringListContains(fieldsChangedVariablePath, this.formControl.value),
          ]);
        }
        return new And([new AlwaysTrue()]);
    }
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.formControl];
  }

  isFormValid(): boolean {
    return this.formControl.valid;
  }
}
